import {IOrderGridItemModel} from "../../../pages/orders/order.models";
import {
    GridColumn,
    GridColumn as Column,
    GridCustomCellProps,
    GridCustomHeaderCellProps
} from "@progress/kendo-react-grid";
import React, {MouseEventHandler} from "react";
import {useAuthenticationContext} from "../../../utils/auth/AuthenticationContext";
import {toString as kendoToString} from "@progress/kendo-intl";
import {isValid, parseISO} from "date-fns";
import {ProgressBar} from "@progress/kendo-react-progressbars";
import FlintCheckBox from "../../controls/FlintCheckBox";
import {FontAwesomeIcon, FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
import * as icons from "@fortawesome/free-solid-svg-icons"
import FlintButton from "@components/controls/FlintButton";
import {ThemeColoursType} from "@utils/AppConstants";
import {IconDefinition} from "@fortawesome/free-regular-svg-icons";
import {IStatusId, SelectionCellHeaderProps} from "@utils/Models";
import {Tooltip} from "@progress/kendo-react-tooltip";
import {CommonUtils} from "@utils/CommonUtils";
import getEnumOrderStatusDescription = CommonUtils.getEnumOrderStatusDescription;
import getEnumOrderStatusIcon = CommonUtils.getEnumOrderStatusIcon;
import {useTranslation} from "react-i18next";

export default function useVivoGridColumns() {
    const {rootModel} = useAuthenticationContext();
    const {t} = useTranslation();
    const segmentCell = (props: any) => {
        const {className, ...other} = props.tdProps;
        const dataItem: IOrderGridItemModel = props.dataItem;
        let c = className + ` color-s-${props.dataItem.DivisionId}${dataItem.DivisionId != rootModel!.CurrentUserProfile.DivisionId ? " other-segment" : ""}`;
        return (
            <td colSpan={1} className={c}
                {...other}>
                {props.children}
            </td>
        );
    }

    const segmentColumn = function (showAllDivisions: boolean) {
        if (rootModel!.CurrentUserProfile.DivisionList.length < 2)
            return;
        if (!showAllDivisions)
            return;
        return <Column headerClassName={"hdr-text-center"} className={"text-center"} title="Seg"
                       cells={{data: segmentCell}} field={"Division"}/>
    }
    const labCell = (props: any) => {
        const {className, ...other} = props.tdProps;
        const dataItem: IOrderGridItemModel = props.dataItem;
        let c = className + ` ${dataItem.LabId != rootModel!.CurrentUserProfile.LabId ? " other-lab" : ""}`;
        return (
            <td colSpan={1} className={c}
                {...other}>
                {props.children}
            </td>
        );
    }

    const labColumn = function (showAllLabs: boolean) {
        if (rootModel!.LabData.LabList.length < 2)
            return;
        if (!showAllLabs)
            return;
        return <Column headerClassName={"hdr-text-center"} className={"text-center"} title="Lab" field={"Lab"}
                       cells={{data: labCell}}/>
    }
    const dateCell = (props: GridDateColumnProps) => {
        const rawDate = props.children;
        const date = rawDate ? parseISO(rawDate + 'Z') : null; // Parse the date string to a Date object
        const isValidDate = date && isValid(date); // Check if the parsed date is valid
        const {tooltipDateFormat = "yyyy-MM-dd", displayDateFormat = "yyyy-MM-dd", ...other} = props;
        return (
            <td
                colSpan={1}
                {...other.tdProps}
                title={isValidDate ? kendoToString(date, tooltipDateFormat) : ""}>
                {isValidDate ? kendoToString(date, displayDateFormat) : ""}
            </td>
        );
    }
    const dateColumn = function (header: string, field: string, displayDateFormat?: string, tooltipDateFormat?: string) {
        return <Column title={header}
                       field={field} 
                       cells={{
            data: (p: GridCustomCellProps) => dateCell({
                ...p,
                displayDateFormat,
                tooltipDateFormat
            })
        }}/>
    }
    const progressCell = (props: any) => {
        const progress = props.dataItem?.Progress ?? 0;
        return (
            <td
                colSpan={1}
                {...props.tdProps}>
                <ProgressBar value={progress}/>
            </td>
        );
    }
    const progressColumn = function (title: string, field: string) {
        return <Column groupable={false} className={"text-center"} headerClassName={"hdr-text-center"} title={title}
                       field={field} cells={{data: progressCell}}/>
    }
    const gridCheckBoxColumnCell = (props: GridCheckBoxColumnProps) => {
        return (
            <td {...props.tdProps}>
                <div className="cb-column text-center">
                    {/*{props.children}*/}
                    <FlintCheckBox disabled={true} checked={props.dataItem[props.field || ""]}/>
                </div>
            </td>
        );
    }

    const checkBoxColumn = function (title: string, field: string, disabled: boolean = true) {
        return <GridColumn editable={true} title={title} field={field}
                           cells={{data: gridCheckBoxColumnCell}}/>
    }

    interface SelectionCellProps extends GridCustomCellProps {
    }

    //interface SelectionCellHeaderProps extends GridCustomHeaderCellProps {


    const CustomCell = (props: SelectionCellProps) => {
        return props.tdProps ? (
            <td
                {...props.tdProps}
                colSpan={1}
                style={{
                    textAlign: "center"
                }}
            >
                {props.children}
            </td>
        ) : null;
    };

    const IconHeaderCell = (props: SelectionCellHeaderProps) => {
        const {themeColor = "", icon = icons.faPrint} = props;
        return <th colSpan={1} className={"text-center"}>
            {props.iconVisible &&
                <FlintButton themeColor={props.themeColor}
                             fillMode={"outline"}
                             size={"small"}
                             onClick={props.onClick}
                             title={props.buttonTitle}>
                    <FontAwesomeIcon icon={icon}/>
                </FlintButton>}
        </th>

    };

    const GridSelectionCell = (props: GridCustomCellProps) => <CustomCell {...props} />;
    const GridSelectionHeaderCell = (props: SelectionCellHeaderProps) =>
        <IconHeaderCell {...props} />;
    const statusCell = (props: any) => {
        const dataItem : IStatusId = props.dataItem;
        const c = `text-center text-status-${dataItem.StatusId}`;
        const statusIcon = getEnumOrderStatusIcon(dataItem.StatusId);
        return (
            <td colSpan={1} {...props.tdProps}>
                <Tooltip>
                    <span title={t(getEnumOrderStatusDescription(dataItem.StatusId))}>
                        <FontAwesomeIcon className={c} icon={statusIcon}/>
                    </span>
                </Tooltip>
            </td>
        );
    }
    const OrderStatusColumn = function () {
        return <Column headerClassName={"hdr-text-center"}
                       title={t("lblStatus")}
                       className={"text-center"}
                       field={"StatusId"}
                       cells={{data: statusCell}}/>
    }
    return {
        segmentColumn,
        labColumn,
        dateCell,
        dateColumn,
        progressColumn,
        gridCheckBoxColumnCell,
        checkBoxColumn,
        GridSelectionCell,
        GridSelectionHeaderCell,
        OrderStatusColumn
    }
}

export interface GridDateColumnProps extends GridCustomCellProps {
    displayDateFormat?: string,
    tooltipDateFormat?: string,
}

export interface GridCheckBoxColumnProps extends GridCustomCellProps {

}